import React, { PureComponent } from 'react';
import { Button } from 'antd';
import moment from 'moment';
import Hotkeys from 'react-hot-keys';
import RightPopUp from '../components/common/RightPopUp';
import { LabVitalEntity, PatientPersonalProfile } from '../store/types/prescription';
import { Data, LabVitalType, TableDataType } from '../store/types/LabTests';
import {
    buildDateWiseLabVitalArray,
    calculateOutOfRange,
    calculateTotalFilledElements,
    filterAllFilledTableData,
    getAllLabTestsVitalKeys,
    getAllLabVitalKeysNotPresentInLabTest,
    tranformData,
    updateElementAndGetTableObj,
} from '../components/prescriptionPad/rightPanel/workspace/labVitalsUtils';
import LabTestsTable from './LabTestsTable';

type State = {
    labDataNotPresentInTable: LabVitalEntity[];
    tableData: TableDataType[];
    dateColumns: (string | undefined)[];
};

type Props = {
    data: Data[];
    patient?: PatientPersonalProfile;
    labVitals?: LabVitalEntity[];
    closeModal: () => void;
    setLabVital: (labVitals: LabVitalEntity[]) => void;
};

class LabTestsRightPadV2RightPopUp extends PureComponent<Props, State> {
    state: State = {
        labDataNotPresentInTable: [],
        tableData: tranformData(this.props.data, this.props.data) || [],
        dateColumns: [moment().format('YYYY-MM-DD')],
    };

    loadData = () => {
        const { labVitals, data } = this.props;

        const allKeys = getAllLabTestsVitalKeys(data);
        const extraVitalsNotPresentInLabTests = getAllLabVitalKeysNotPresentInLabTest(
            allKeys,
            labVitals,
        );

        this.setState({
            labDataNotPresentInTable: extraVitalsNotPresentInLabTests,
        });

        // calculate labvitals present in the table and load in state
        const labVitalsPresentInTable = labVitals
            ?.filter((labVital) => allKeys[labVital.id])
            .map((labVital) => ({ ...labVital, isComingFromPP: true }));

        if (labVitalsPresentInTable?.length) {
            const dateWiseLabVital = buildDateWiseLabVitalArray(labVitalsPresentInTable);
            const dateArray = Object.values(dateWiseLabVital).map((value) => value.date);

            const tableData = tranformData(data, data, undefined, dateArray.length);

            for (let i = 0; i < dateArray.length; i += 1) {
                for (let j = 0; j < tableData.length; j += 1) {
                    for (let k = 0; k < tableData[j].labVitals.length; k += 1) {
                        const element =
                            dateWiseLabVital[i]?.data?.[tableData[j]?.labVitals?.[k]?.[i]?.id];

                        if (element) {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            tableData[j].labVitals[k][i] = {
                                ...tableData[j].labVitals[k][i],
                                ...element,
                            };
                        }
                    }
                }
            }
            this.setState({ tableData: tableData, dateColumns: dateArray });
        } else {
            this.setState({ tableData: tranformData(data, data) || [] });
        }
    };

    componentDidMount = async () => {
        this.loadData();
    };

    onSaveElementInLocalState = (labVitalObj: Partial<LabVitalType>, i: number) => {
        const { tableData } = this.state;
        this.setState({
            tableData: updateElementAndGetTableObj(tableData, labVitalObj, i),
        });
        return;
    };

    onSaveAllElementInRx = () => {
        const { tableData, dateColumns, labDataNotPresentInTable } = this.state;
        const { setLabVital } = this.props;

        setLabVital([
            ...filterAllFilledTableData(dateColumns, tableData),
            ...labDataNotPresentInTable,
        ]);
    };

    setDateColumns = (dateColumns: (string | undefined)[]) => {
        this.setState({ dateColumns: dateColumns });
    };

    setTableData = (tableData: TableDataType[]) => {
        this.setState({ tableData: tableData });
    };

    render(): React.ReactNode {
        const { data, patient, labVitals, closeModal } = this.props;
        const { dateColumns, tableData, labDataNotPresentInTable } = this.state;
        return (
            <Hotkeys keyName="esc" onKeyDown={closeModal}>
                <RightPopUp closeMe={closeModal}>
                    <div className="flex flex-col h-full w-full">
                        <div className="sticky bg-darwin-neutral-50 flex items-center justify-between w-full py-8 px-20">
                            <span className="font-700 text-neutral-3">Lab Results</span>
                            <div className="flex bg-white px-16 py-9 rounded-8 text-14 items-center space-x-8">
                                <div className="flex space-x-8 items-center">
                                    <span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="4"
                                            height="5"
                                            viewBox="0 0 4 5"
                                            fill="none"
                                        >
                                            <path
                                                d="M2 4.5C0.890625 4.5 0 3.60938 0 2.5C0 1.39844 0.890625 0.5 2 0.5C3.10156 0.5 4 1.39844 4 2.5C4 3.60938 3.10156 4.5 2 4.5Z"
                                                fill="#A3A8C2"
                                            />
                                        </svg>
                                    </span>
                                    <div className="flex items-center space-x-4">
                                        <span className="font-700 text-darwin-accent-bright-yellow-900">
                                            {calculateTotalFilledElements(dateColumns, tableData)}
                                        </span>
                                        <span className="text-darwin-neutral-800">
                                            Filled Lab results
                                        </span>
                                    </div>
                                </div>
                                <div className="flex space-x-8 items-center">
                                    <span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="4"
                                            height="5"
                                            viewBox="0 0 4 5"
                                            fill="none"
                                        >
                                            <path
                                                d="M2 4.5C0.890625 4.5 0 3.60938 0 2.5C0 1.39844 0.890625 0.5 2 0.5C3.10156 0.5 4 1.39844 4 2.5C4 3.60938 3.10156 4.5 2 4.5Z"
                                                fill="#A3A8C2"
                                            />
                                        </svg>
                                    </span>
                                    <div className="flex items-center space-x-4">
                                        <span className="font-700 text-darwin-status-warning-500">
                                            {calculateOutOfRange(dateColumns, tableData)}
                                        </span>
                                        <span className="text-darwin-neutral-800">
                                            Out of range
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <span></span>
                        </div>

                        <div className="h-full w-full px-16 bg-white hide-scrollbar overflow-hidden">
                            <LabTestsTable
                                data={data}
                                patient={patient}
                                labVitals={labVitals}
                                labDataNotPresentInTable={labDataNotPresentInTable}
                                tableData={tableData}
                                dateColumns={dateColumns}
                                setDateColumns={this.setDateColumns}
                                onSaveElementInLocalState={this.onSaveElementInLocalState}
                                setTableData={this.setTableData}
                            />
                            <div className="min-h-200"></div>
                        </div>
                        <div className="bg-darwin-neutral-50 flex items-center justify-between w-full py-8 px-20">
                            <Button
                                onClick={closeModal}
                                className="font-700 text-neutral-3 cursor-pointer bg-darwin-neutral-50"
                            >
                                Close
                            </Button>
                            <div className="flex space-x-16 items-center">
                                <Button
                                    onClick={() => {
                                        this.onSaveAllElementInRx();
                                        closeModal();
                                    }}
                                    className="px-16 py-18 btn--small secondary-btn space-x-8"
                                >
                                    <p>Save in Rx</p>
                                    <svg
                                        className="w-16 h-16 fill-actionPrimary"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                    >
                                        <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                                    </svg>
                                </Button>
                            </div>
                        </div>
                    </div>
                </RightPopUp>
            </Hotkeys>
        );
    }
}

export default LabTestsRightPadV2RightPopUp;
